import { useState, useEffect, useRef } from 'react';

import { marked } from 'marked';
import { toast } from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Error, SubscriptionExpired } from '../../modules/alerts';
import useFreeTrial from '../freetrial/useFreeTrial';
import { chatActions } from '../../lib/redux/chat/slice';
import useRecentMessages from '../api/messages/useRecentMessages';
import useGuidedSuggestedPrompts from './useGuidedSuggestedPrompts';
import { useGetVectorDocumentsQuery } from '../../lib/redux/documents/api-slice';
import { isLoggedIn } from '../../utils/shared';
import useIframeEvents from '../iframe/useIframeEvents';

let chatSocket = null;
let stopGenerationSocket = null;
let LATEST_GENERATION_ID = '';
let responseInProgress = '';
let searchResults = '';
let vectorDocuments = null;

export default function useChatRoom() {
  const chat = useSelector((state) => state.chat.data);
  const pageContent = useSelector((state) => state.chat.pageContent);
  const edxUsageId = useSelector((state) => state.chat.edxUsageId);
  const edxCourseId = useSelector((state) => state.chat.edxCourseId);
  const globalGenerating = useSelector((state) => state.chat.generating);
  const globalStreaming = useSelector((state) => state.chat.streaming);
  const sessionId = useSelector((state) => state.chat.sessionId);
  const allMessages = useSelector((state) => state.chat.messages);
  const tabbedMessages = useSelector((state) => state.chat.tabbedMessages);
  const tabbedSessions = useSelector((state) => state.chat.tabbedSessions);
  const mentors = useSelector((state) => state.mentors);
  const auth = useSelector((state) => state.auth?.data);
  const embed = useSelector((state) => state.embed);
  const subscriptionExpired = useSelector(
    (state) => state.navigation.subscriptionExpired
  );
  const dispatch = useDispatch();
  const [aiResponseInProgress, setAiResponseInProgress] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [doneResponding, setDoneResponding] = useState(false);
  const { loadGuidedPrompts } = useGuidedSuggestedPrompts();
  const { reloadRecentMessages } = useRecentMessages();
  const [error, setError] = useState('');
  const pathParams = useParams();

  const { reloadFreeTrialCount } = useFreeTrial(true);
  const { sendMessageToParent } = useIframeEvents();

  const [chatPayload, setChatPayload] = useState({
    flow: {
      name: mentors?.mentor?.unique_id,
      tenant: pathParams?.platform ? pathParams.platform : auth?.tenant?.key,
      username: auth?.user?.user_nicename,
      pathway: mentors?.mentor?.unique_id,
    },
    session_id:
      embed.mode === 'advanced'
        ? tabbedSessions[embed.advanceActiveTab]
        : sessionId,
    token: localStorage.getItem('axd_token'),
  });

  const { refetch } = useGetVectorDocumentsQuery(
    {
      sessionId:
        embed.mode === 'advanced'
          ? tabbedSessions[embed.advanceActiveTab]
          : sessionId,
      org: auth?.tenant?.key,
      userId: auth?.user?.user_nicename,
    },
    {
      skip:
        !(embed.mode === 'advanced'
          ? tabbedSessions[embed.advanceActiveTab]
          : sessionId) || !isLoggedIn(),
    }
  );

  useEffect(() => {
    if (embed.advanceActiveTab && tabbedSessions[embed.advanceActiveTab]) {
      setChatPayload({
        ...chatPayload,
        flow: {
          ...chatPayload.flow,
          name: mentors?.mentor?.unique_id,
          pathway: mentors?.mentor?.unique_id,
        },
        session_id: tabbedSessions[embed.advanceActiveTab],
      });
    } else {
      setChatPayload({
        ...chatPayload,
        flow: {
          ...chatPayload.flow,
          name: mentors?.mentor?.unique_id,
          pathway: mentors?.mentor?.unique_id,
        },
        session_id: sessionId,
      });
    }
  }, [sessionId, tabbedSessions, embed.advanceActiveTab, mentors?.mentor]);

  const getLatestPrompt = () => {
    let allMsg = [];
    if (embed.advanceActiveTab) {
      allMsg = [...tabbedMessages[embed.advanceActiveTab]]?.reverse();
    } else {
      allMsg = [...allMessages]?.reverse();
    }
    const lastHumanMsg = allMsg.find((item) => item.type === 'human');
    if (lastHumanMsg) {
      return lastHumanMsg.content;
    }
  };

  const autoScrollIfNecessary = () => {
    const msgElements = document.getElementsByClassName('upper-area');
    if (msgElements?.length > 0) {
      const last_msg = msgElements[0];
      if (last_msg) {
        last_msg.scrollTop = last_msg.scrollHeight;
      }
    }
  };

  const _handleAddUserPromptToMessages = (hide = false, content = null) => {
    const proActiveSession = window.proActiveSession;
    if (proActiveSession) {
      hide = true;
    }
    const message = {
      type: 'human',
      content: content || chat?.prompt,
      hide: hide,
    };
    if (embed.mode === 'advanced' && embed.advanceActiveTab) {
      dispatch(
        chatActions.tabbedMessagesUpdated({
          ...tabbedMessages,
          [embed.advanceActiveTab]: [
            ...(tabbedMessages[embed.advanceActiveTab].filter(
              (msg) => msg.type !== 'static' // once a prompt is static, no need showing it when the a prompt is sent
            ) ?? []),
            message,
          ],
        })
      );
    } else {
      const messages = [...(allMessages ?? []), message];
      dispatch(chatActions.messagesUpdated(messages));
    }
  };

  const _handleAddSearchSources = () => {
    if (searchResults) {
      const allMsg =
        embed.mode === 'advanced' && embed.advanceActiveTab
          ? [...tabbedMessages[embed.advanceActiveTab]]
          : [...allMessages];
      const lastMsg = allMsg.pop();
      if (lastMsg?.generation_id === LATEST_GENERATION_ID) {
        const updatedLastMsg = {
          ...lastMsg,
          searchResults: searchResults,
        };
        allMsg.push(updatedLastMsg);
        if (embed.mode === 'advanced' && embed.advanceActiveTab) {
          dispatch(
            chatActions.tabbedMessagesUpdated({
              ...tabbedMessages,
              [embed.advanceActiveTab]: allMsg,
            })
          );
        } else {
          dispatch(chatActions.messagesUpdated(allMsg));
        }
      }
      searchResults = '';
    }
  };

  const getRefreshCount = () => {
    const refreshCount = localStorage.getItem('refreshCount');
    if (refreshCount) {
      return Number(refreshCount);
    }
    return 0;
  };

  const _handleShowVectorDocuments = () => {
    dispatch(chatActions.vectorDocumentsUpdated(vectorDocuments));
  };

  useEffect(() => {
    if (doneResponding) {
      setTimeout(() => {
        _handleAddSearchSources();
        _handleShowVectorDocuments();
        sendMessageToParent({
          type: 'MENTOR:RESPONDED',
          value: {
            sessionId,
          },
        });
      }, 1000);
      setDoneResponding(false);
    }
  }, [doneResponding]);

  useEffect(() => {
    if (error) {
      console.error(
        `CHAT_ERROR: ${error}; URL: ${chatSocket?.url}; REQUEST_DATA: ${JSON.stringify(chatPayload)}; PROMPT: ${getLatestPrompt()}; TOKEN: ${localStorage.getItem('axd_token')?.substring(0, -5)}...`
      );
    }
  }, [error]);

  useEffect(() => {
    if (doneResponding && isLoggedIn()) {
      refetch();
    }
  }, [doneResponding, refetch]);

  const _handleIncomingMessage = (event) => {
    let response = JSON.parse(event.data);

    if (response?.detail === 'Connected.') {
      _handleSendTextQuery();
    }
    autoScrollIfNecessary();
    if (response?.generation_id) {
      LATEST_GENERATION_ID = response?.generation_id;
    }

    if (response?.data) {
      setStreaming(true);
      responseInProgress += response?.data;
      setAiResponseInProgress(responseInProgress);
      setGenerating(false);
    }

    if (response?.eos) {
      responseInProgress = '';
      _endConnection();
      setDoneResponding(true);
      loadGuidedPrompts();
    }

    if (response?.error) {
      setError(response?.error);

      toast.custom(Error(response.error), {
        ariaProps: { role: 'alert' },
      });
      setGenerating(false);
      setStreaming(false);
      _endConnection();
      setDoneResponding(true);
    }

    if (response?.type === 'search_results') {
      searchResults = response.results;
    }

    if (response?.type === 'vector_results') {
      vectorDocuments = response.results;
    }
  };

  const _handleResetPrompt = () => {
    dispatch(chatActions.chatUpdated({ ...chat, prompt: '' }));
  };

  const _initiateConnection = () => {
    try {
      chatSocket?.send(JSON.stringify(chatPayload));
    } catch (e) {
      console.log(e);
    }
  };

  const _handleSendTextQuery = () => {
    const proActiveSession = window.proActiveSession;
    const payload = { ...chatPayload, prompt: chat.prompt };
    if (pageContent) {
      payload.page_content = pageContent;
    }
    if (edxCourseId) {
      payload.edx_course_id = edxCourseId;
      payload.edx_usage_id = edxUsageId;
    }
    chatSocket?.send(JSON.stringify(payload));
    if (proActiveSession) {
      window.proActiveSession = null;
    }
    if (!embed.advanceActiveTab) {
      _resetPageContent();
    }
    setError('');
  };

  const _endConnection = () => {
    if (chatSocket?.readyState === WebSocket.OPEN) {
      chatSocket?.close();
    }
    setGenerating(false);
    setStreaming(false);
    window.proActiveSession = null;
  };

  const handleStopRespondingBtnClick = () => {
    _handleOpenStopGenerationConnection();
  };

  useEffect(() => {
    if (embed.mode === 'advanced' && embed.advanceActiveTab) {
      if (tabbedMessages[embed.advanceActiveTab]?.length > 0) {
        const messages = [...tabbedMessages[embed.advanceActiveTab]];
        if (messages?.length > 0) {
          const lastMsg = messages.pop();
          if (lastMsg.type === 'human') {
            dispatch(chatActions.chatUpdated({ ...chat, prompt: '' }));
          }
        }
      }
    } else {
      if (allMessages?.length > 0) {
        const messages = [...allMessages];
        if (messages?.length > 0) {
          const lastMsg = messages.pop();
          if (lastMsg.type === 'human') {
            dispatch(chatActions.chatUpdated({ ...chat, prompt: '' }));
          }
        }
      }
    }
  }, [allMessages, tabbedMessages, embed.advanceActiveTab, dispatch]);

  const effectRan = useRef(false);

  useEffect(() => {
    if (
      (allMessages?.length > 0 ||
        (embed.mode === 'advanced' &&
          embed.advanceActiveTab &&
          tabbedMessages[embed.advanceActiveTab].length > 0)) &&
      globalGenerating === false &&
      globalStreaming === false &&
      !effectRan.current
    ) {
      effectRan.current = true;
      reloadFreeTrialCount();
      reloadRecentMessages();
    }

    return () => {
      effectRan.current = false;
    };
  }, [
    allMessages,
    tabbedMessages,
    embed.advanceActiveTab,
    globalStreaming,
    globalGenerating,
  ]);

  useEffect(() => {
    autoScrollIfNecessary();
  }, [allMessages, tabbedMessages, embed.advanceActiveTab]);

  useEffect(() => {
    let mounted = true;
    if (aiResponseInProgress && mounted) {
      const content = marked.parse(aiResponseInProgress);
      let allMsg =
        embed.mode === 'advanced' && embed.advanceActiveTab
          ? [...tabbedMessages[embed.advanceActiveTab]]
          : [...(allMessages ?? [])];
      let lastMsg = allMsg.pop();

      // If yes then the last message in display is a response in progress
      if (lastMsg?.generation_id === LATEST_GENERATION_ID) {
        const updatedMsg = {
          ...lastMsg,
          content,
          streaming,
          rawContent: aiResponseInProgress,
        };
        allMsg.push(updatedMsg);
      } else {
        // Means the last message is not a response in progress. Put it back and add the new one
        allMsg.push(lastMsg);
        allMsg.push({
          type: 'ai',
          content: content,
          generation_id: LATEST_GENERATION_ID,
          streaming: streaming,
          rawContent: aiResponseInProgress,
        });
      }
      if (embed.mode === 'advanced' && embed.advanceActiveTab) {
        dispatch(
          chatActions.tabbedMessagesUpdated({
            ...tabbedMessages,
            [embed.advanceActiveTab]: allMsg,
          })
        );
      } else {
        dispatch(chatActions.messagesUpdated(allMsg));
      }
    }

    return () => {
      mounted = true;
    };
  }, [chat?.generating, aiResponseInProgress, streaming, dispatch]);

  const _initiateServerStopGeneration = () => {
    stopGenerationSocket.send(
      JSON.stringify({
        generation_id: LATEST_GENERATION_ID,
        name: mentors?.mentor?.name,
        tenant: auth?.tenant?.key,
        username: auth?.user?.user_nicename,
        token: localStorage.getItem('axd_token'),
      })
    );
  };

  const handleStopGenerationSocketMessage = (event) => {
    let response = JSON.parse(event.data);
    if (response?.detail === 'Stopped') {
      responseInProgress = '';
      setStreaming(false);
      stopGenerationSocket.close();
      _endConnection();
      loadGuidedPrompts();
    }
  };

  const _handleOpenStopGenerationConnection = () => {
    stopGenerationSocket = new WebSocket(
      `${process.env.REACT_APP_BASE_WS_URL}/ws/langflow-stop-generation/`
    );
    stopGenerationSocket.onopen = _initiateServerStopGeneration;
    stopGenerationSocket.onmessage = handleStopGenerationSocketMessage;
  };

  const _handleOpenWebsocketConnection = () => {
    setGenerating(true);
    chatSocket = new WebSocket(
      `${process.env.REACT_APP_BASE_WS_URL}/ws/langflow/`
    );
    chatSocket.onerror = () => {
      toast.custom(Error('Unable to connect to server'), {
        ariaProps: { role: 'alert' },
      });
      _endConnection();
    };
    chatSocket.onmessage = _handleIncomingMessage;
    chatSocket.onopen = () => {
      setTimeout(_initiateConnection, 1000);
    };
  };

  const handleUserPromptFormSubmit = (event) => {
    event?.preventDefault();
    if (subscriptionExpired) {
      toast.custom(
        SubscriptionExpired(
          'Your free trial has ended. Please upgrade to continue using mentorAI 😎.'
        )
      );
      return;
    }
    if (chat?.prompt) {
      _handleAddUserPromptToMessages();
      _handleOpenWebsocketConnection();
    }
  };

  const _resetPageContent = () => {
    dispatch(chatActions.pageContentUpdate(null));
  };

  const handlePromptChange = (event) => {
    const prompt = event.target.value;
    dispatch(chatActions.chatUpdated({ ...chat, prompt: prompt }));
  };

  return {
    generating,
    handleUserPromptFormSubmit,
    handlePromptChange,
    streaming,
    handleStopRespondingBtnClick,
  };
}
